import { useState, type FormEvent } from 'react';
import type { BuildTrigger, Environment } from '@hangar/shared';
import { toast } from 'sonner';
import type { Profile } from '@/api/schemasProfile';
import { useBuildTriggerMutations } from '@/api/hooksTriggers';
import { FormField, fieldA11y } from '@/components/FormField';
import { OptionSelect } from '@/components/OptionSelect';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { bodyOf, environmentsOf, triggerFormErrors, type TriggerFormErrors, type TriggerFormValues } from './buildTriggersModel';

export interface TriggerDialogState { mode: 'create' | 'edit'; trigger?: BuildTrigger; values: TriggerFormValues }

interface Props {
  appId: string;
  state: TriggerDialogState | null;
  profiles: readonly Profile[];
  environments: readonly Environment[];
  onOpenChange: (open: boolean) => void;
}

/** Add or edit one build trigger: branch pattern, paths, profile, environment, on/off (5 fields: a dialog, not a page). */
export function BuildTriggerDialog({ appId, state, profiles, environments, onOpenChange }: Props) {
  return (
    <Dialog open={Boolean(state)} onOpenChange={onOpenChange}>
      <DialogContent data-testid="trigger-dialog" aria-describedby="trigger-dialog-desc">
        {state ? <TriggerForm key={state.trigger?.id ?? 'new'} appId={appId} state={state} profiles={profiles} environments={environments} onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function useTriggerForm(appId: string, state: TriggerDialogState, environments: readonly Environment[], onDone: () => void) {
  const [values, setValues] = useState(state.values);
  const [errors, setErrors] = useState<TriggerFormErrors>({});
  const { create, update } = useBuildTriggerMutations(appId);
  const mutation = state.mode === 'create' ? create : update;
  const set = <K extends keyof TriggerFormValues>(key: K, value: TriggerFormValues[K]) => {
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const found = triggerFormErrors(values, environments);
    setErrors(found);
    if (Object.values(found).some(Boolean)) return;
    const done = { onSuccess: () => { toast.success(state.mode === 'create' ? 'Build trigger added' : 'Build trigger saved'); onDone(); } };
    if (state.mode === 'create') create.mutate(bodyOf(values), done);
    else update.mutate({ id: state.trigger!.id, body: bodyOf(values) }, done);
  };
  return { values, errors, set, onSubmit, pending: mutation.isPending, problem: mutation.error };
}

type Form = ReturnType<typeof useTriggerForm>;

function BranchField({ f }: { f: Form }) {
  return (
    <FormField id="trigger-branch" label="Branch" required hint="A branch name, or a pattern with * (release/*)" error={f.errors.branchPattern}>
      <Input data-testid="trigger-branch" value={f.values.branchPattern} maxLength={100} autoComplete="off" spellCheck={false} className="font-mono" onChange={(e) => f.set('branchPattern', e.target.value)} {...fieldA11y('trigger-branch', f.errors.branchPattern, 'hint')} />
    </FormField>
  );
}

function PathsField({ f }: { f: Form }) {
  return (
    <FormField id="trigger-paths" label="Only when these paths change" hint="One per line, * matches anything. Empty: every push builds" error={f.errors.paths}>
      <Textarea data-testid="trigger-paths" value={f.values.paths} rows={3} spellCheck={false} className="font-mono text-[13px]" placeholder="apps/mobile/*" onChange={(e) => f.set('paths', e.target.value)} {...fieldA11y('trigger-paths', f.errors.paths, 'hint')} />
    </FormField>
  );
}

function TargetFields({ f, profiles, environments }: { f: Form; profiles: readonly Profile[]; environments: readonly Environment[] }) {
  const profile = profiles.find((p) => p.id === f.values.profileId);
  const envs = environmentsOf(profile, environments);
  const pickProfile = (id: string) => {
    f.set('profileId', id);
    const next = environmentsOf(profiles.find((p) => p.id === id), environments);
    if (!next.some((e) => e.id === f.values.environmentId)) f.set('environmentId', next[0]?.id ?? '');
  };
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <FormField id="trigger-profile" label="Build profile" required error={f.errors.profileId}>
        <OptionSelect data-testid="trigger-profile" className="w-full" value={f.values.profileId} onValueChange={pickProfile} placeholder="Pick a profile"
          options={profiles.map((p) => ({ value: p.id, label: p.name }))} {...fieldA11y('trigger-profile', f.errors.profileId)} />
      </FormField>
      <FormField id="trigger-environment" label="Environment" required error={f.errors.environmentId}>
        <OptionSelect data-testid="trigger-environment" className="w-full" value={f.values.environmentId} onValueChange={(v) => f.set('environmentId', v)} placeholder="Pick an environment"
          options={envs.map((e) => ({ value: e.id, label: e.isProtected ? `${e.name} (protected)` : e.name }))} {...fieldA11y('trigger-environment', f.errors.environmentId)} />
      </FormField>
    </div>
  );
}

function EnabledField({ f }: { f: Form }) {
  return (
    <label htmlFor="trigger-enabled" className="flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-lg border px-3.5 py-2.5">
      <span className="flex flex-col">
        <span className="text-[14px] font-medium text-foreground">On</span>
        <span className="text-[13px] text-muted-foreground">Off keeps the rule without starting builds</span>
      </span>
      <Switch id="trigger-enabled" data-testid="trigger-enabled" checked={f.values.enabled} onCheckedChange={(v) => f.set('enabled', v)} />
    </label>
  );
}

function TriggerForm({ appId, state, profiles, environments, onDone }: { appId: string; state: TriggerDialogState; profiles: readonly Profile[]; environments: readonly Environment[]; onDone: () => void }) {
  const f = useTriggerForm(appId, state, environments, onDone);
  return (
    <form data-testid="trigger-form" onSubmit={f.onSubmit} noValidate className="flex min-h-0 flex-col" aria-label={state.mode === 'create' ? 'Add build trigger' : 'Edit build trigger'}>
      <DialogHeader>
        <DialogTitle>{state.mode === 'create' ? 'Add build trigger' : 'Edit build trigger'}</DialogTitle>
        <DialogDescription id="trigger-dialog-desc">A push to the branch starts a build with this profile. Pushes within 30 s build only the newest commit.</DialogDescription>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        <BranchField f={f} />
        <PathsField f={f} />
        <TargetFields f={f} profiles={profiles} environments={environments} />
        <EnabledField f={f} />
        <ProblemAlert error={f.problem} testId="trigger-problem" />
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onDone}>Cancel</Button>
        <Button type="submit" data-testid="trigger-submit" loading={f.pending}>{state.mode === 'create' ? 'Add trigger' : 'Save'}</Button>
      </DialogFooter>
    </form>
  );
}
